import { useEffect, useRef, useState } from "react";
import {
    Button,
    Col,
    Form,
    Input,
    message,
    Popconfirm,
    Row,
    Switch,
    Table,
    Tag,
    Tooltip,
} from "antd";
import {
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    FormOutlined,
    PlusCircleOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import {
    addMaster,
    deleteMaster,
    fetchMaster,
    isDuplicate,
    updateMaster,
} from "./Masterapi";
import {
    AMBER,
    EMERALD,
    INDIGO,
    PURPLE,
    ROSE,
    ROW_COLORS,
    SKY,
    getStyles,
    rowStyles,
} from "./Mastertheme";

/* ───────────────────────────────────────────────────────────────
   config = {
     master,        // entry from MASTERS (masterApi.js)
     title,         // "Brand Master"
     singular,      // "Brand"
     plural,        // "Brands"
     subtitle,
     icon,          // header icon node
     from, to,      // gradient colours
     fields: [{ name, label, placeholder, required, type, icon, minLen }]
              type: "text" (default) | "number" | "switch"
     tips: [string]
   }
   ─────────────────────────────────────────────────────────────── */
export default function MasterCrudScreen({ config }) {
    const {
        master,
        title,
        singular,
        plural,
        subtitle,
        icon,
        from,
        to,
        fields,
        tips = [],
    } = config;

    const G = getStyles(from, to);
    const [form] = Form.useForm();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [filter, setFilter] = useState("");
    const refs = useRef({});

    const mainField = fields[0];
    const focusFirst = (ms = 100) =>
        setTimeout(() => refs.current[mainField.name]?.focus?.(), ms);

    /* ── fetch ───────────────────────────────────────────────── */
    const fetchData = async () => {
        setLoading(true);
        try {
            setTableData(await fetchMaster(master));
        } catch {
            message.error(`Failed to load ${plural.toLowerCase()}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        focusFirst(300);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── derived ─────────────────────────────────────────────── */
    const q = filter.trim().toLowerCase();
    const filteredData = q
        ? tableData.filter((r) =>
            fields.some((f) => String(r[f.name] ?? "").toLowerCase().includes(q)),
        )
        : tableData;

    const editingRecord =
        editingKey != null ? tableData.find((r) => r.key === editingKey) : null;

    /* ── handlers ────────────────────────────────────────────── */
    const handleEdit = (record) => {
        setEditingKey(record.key);
        const vals = {};
        fields.forEach((f) => {
            const v = record[f.name];
            vals[f.name] = f.type === "switch" ? !!v : v === "-" ? "" : v;
        });
        form.setFieldsValue(vals);
        focusFirst();
    };

    const handleCancel = () => {
        form.resetFields();
        setEditingKey(null);
        setFilter("");
        focusFirst();
    };

    const handleRefresh = () => {
        handleCancel();
        fetchData();
    };

    const handleSubmit = async (values) => {
        if (isDuplicate(master, tableData, values, editingKey)) {
            message.warning(`${singular} already exists`);
            return;
        }
        setSaving(true);
        try {
            if (editingKey != null && editingRecord) {
                await updateMaster(master, editingRecord, values);
            } else {
                await addMaster(master, values);
            }
            message.success(
                `${singular} ${editingKey != null ? "updated" : "added"} successfully`,
                2,
            );
            handleCancel();
            fetchData();
        } catch (err) {
            message.error(err?.response?.data?.message || `Failed to save ${singular.toLowerCase()}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (record) => {
        try {
            await deleteMaster(master, record);
            message.success(`${singular} deleted`);
            if (record.key === editingKey) handleCancel();
            fetchData();
        } catch {
            message.error("Delete failed");
        }
    };

    /* ── columns (generated from fields) ─────────────────────── */
    const fieldColumns = fields.map((f, i) => ({
        title: (
            <span
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    color: i === 0 ? from : SKY,
                }}
            >
                {f.icon} {f.label}
            </span>
        ),
        dataIndex: f.name,
        width: i === 0 ? undefined : 150,
        render: (v) => {
            if (f.type === "switch")
                return (
                    <Tag color={v ? "success" : "default"} style={{ borderRadius: 6 }}>
                        {v ? "Yes" : "No"}
                    </Tag>
                );
            if (i === 0)
                return (
                    <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
                        {v}
                    </span>
                );
            const empty = v === "-" || v === "" || v == null;
            return empty ? (
                <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
            ) : (
                <Tag
                    style={{
                        background: `${SKY}15`,
                        color: SKY,
                        border: `1px solid ${SKY}44`,
                        borderRadius: 6,
                        fontWeight: 600,
                        fontSize: 12,
                    }}
                >
                    {v}
                </Tag>
            );
        },
    }));

    const columns = [
        {
            title: "#",
            width: 58,
            align: "center",
            render: (_, __, idx) => {
                const color = ROW_COLORS[idx % ROW_COLORS.length];
                return (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            background: `${color}18`,
                            color,
                            fontWeight: 700,
                            fontSize: 12,
                        }}
                    >
                        {idx + 1}
                    </span>
                );
            },
        },
        ...fieldColumns,
        {
            title: "Actions",
            width: 100,
            align: "center",
            render: (_, record) => (
                <div
                    style={{ display: "flex", gap: 6, justifyContent: "center" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip title="Edit">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            style={{
                                background: `${INDIGO}15`,
                                color: INDIGO,
                                border: `1px solid ${INDIGO}33`,
                                borderRadius: 8,
                            }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={`Delete this ${singular.toLowerCase()}?`}
                        description="This action cannot be undone."
                        onConfirm={() => handleDelete(record)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Delete">
                            <Button
                                size="small"
                                icon={<DeleteOutlined />}
                                style={{
                                    background: `${ROSE}15`,
                                    color: ROSE,
                                    border: `1px solid ${ROSE}33`,
                                    borderRadius: 8,
                                }}
                            />
                        </Tooltip>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    /* ── form item renderer ──────────────────────────────────── */
    const renderField = (f, i) => {
        const isLast = i === fields.length - 1;
        const focusNext = () => {
            const next = fields.slice(i + 1).find((x) => x.type !== "switch");
            if (next) refs.current[next.name]?.focus?.();
            else form.submit();
        };
        const label = (
            <span style={{ fontWeight: 600, color: "#374151", fontSize: 13 }}>
                {f.label} {f.required && <span style={{ color: ROSE }}>*</span>}
            </span>
        );

        if (f.type === "switch") {
            return (
                <Form.Item
                    key={f.name}
                    label={label}
                    name={f.name}
                    valuePropName="checked"
                    initialValue={false}
                >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
            );
        }

        return (
            <Form.Item
                key={f.name}
                label={label}
                name={f.name}
                rules={[
                    ...(f.required ? [{ required: true, message: `${f.label} is required` }] : []),
                    ...(f.minLen ? [{ min: f.minLen, message: `At least ${f.minLen} characters` }] : []),
                ]}
            >
                <Input
                    ref={(el) => (refs.current[f.name] = el)}
                    type={f.type === "number" ? "number" : "text"}
                    min={f.type === "number" ? 0 : undefined}
                    placeholder={f.placeholder}
                    prefix={f.icon && <span style={{ color: i === 0 ? from : SKY, fontSize: 15 }}>{f.icon}</span>}
                    style={G.inputStyle}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            isLast ? form.submit() : focusNext();
                        }
                    }}
                />
            </Form.Item>
        );
    };

    /* ── render ──────────────────────────────────────────────── */
    return (
        <div style={G.page}>
            {/* Header */}
            <div style={G.headerBar}>
                <div style={G.headerLeft}>
                    <div style={G.headerIcon}>{icon}</div>
                    <div>
                        <h1 style={G.headerTitle}>{title}</h1>
                        <p style={G.headerSub}>{subtitle}</p>
                    </div>
                </div>
                <div style={G.statsRow}>
                    <div style={G.stat(from)}>
                        <span style={G.statNum(from)}>{tableData.length}</span>
                        <span style={G.statLabel}>Total</span>
                    </div>
                    <div style={G.stat(SKY)}>
                        <span style={G.statNum(SKY)}>{filteredData.length}</span>
                        <span style={G.statLabel}>Filtered</span>
                    </div>
                </div>
            </div>

            <Row gutter={[20, 20]} align="top">
                {/* Table panel */}
                <Col xs={24} xl={16}>
                    <div style={G.card}>
                        <div style={G.panelHead(from, to)}>
                            <span style={G.panelTitle}>
                                <UnorderedListOutlined style={{ fontSize: 16 }} />
                                All {plural}
                            </span>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    background: "rgba(255,255,255,0.18)",
                                    borderRadius: 20,
                                    padding: "3px 12px 3px 8px",
                                    gap: 6,
                                    minWidth: 220,
                                }}
                            >
                                <SearchOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }} />
                                <input
                                    className="mc-search"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder={`Search ${plural.toLowerCase()}…`}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        outline: "none",
                                        color: "#fff",
                                        fontSize: 13,
                                        width: "100%",
                                    }}
                                />
                                {filter && (
                                    <CloseCircleOutlined
                                        style={{ color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 13 }}
                                        onClick={() => setFilter("")}
                                    />
                                )}
                            </div>
                        </div>

                        <div style={{ padding: "4px 0" }}>
                            <Table
                                columns={columns}
                                dataSource={filteredData}
                                loading={loading}
                                size="middle"
                                pagination={false}
                                scroll={{ x: 600, y: "calc(100vh - 320px)" }}
                                rowClassName={(r) => (r.key === editingKey ? "mc-row-selected" : "mc-row")}
                                onRow={(record) => ({
                                    onClick: () => handleEdit(record),
                                    style: { cursor: "pointer" },
                                })}
                            />
                        </div>
                    </div>
                </Col>

                {/* Form panel */}
                <Col xs={24} xl={8}>
                    <div style={G.card}>
                        <div style={G.panelHead(PURPLE, INDIGO)}>
                            <span style={G.panelTitle}>
                                <FormOutlined style={{ fontSize: 16 }} />
                                {editingKey != null ? `Edit ${singular}` : `Add New ${singular}`}
                            </span>
                            {editingKey != null && (
                                <Tooltip title="Cancel editing">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        style={{
                                            background: "rgba(255,255,255,0.2)",
                                            border: "none",
                                            color: "#fff",
                                            borderRadius: 8,
                                            width: 28,
                                            height: 28,
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 14,
                                        }}
                                    >
                                        <CloseCircleOutlined />
                                    </button>
                                </Tooltip>
                            )}
                        </div>

                        <div style={G.panelBody}>
                            {editingKey == null ? (
                                <div style={G.hint}>
                                    <PlusCircleOutlined style={{ color: from, fontSize: 15 }} />
                                    <span>Fill in the details below to add a new {singular.toLowerCase()}.</span>
                                </div>
                            ) : (
                                editingRecord && (
                                    <div style={G.selectedBadge(from)}>
                                        <div style={G.badgeAvatar(from)}>{icon}</div>
                                        <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>
                                            {editingRecord[mainField.name]}
                                        </div>
                                    </div>
                                )
                            )}

                            <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                                <div style={G.sectionLabel(INDIGO)}>
                                    <span style={G.dot(INDIGO)} />
                                    {singular} Details
                                </div>

                                {fields.map(renderField)}

                                <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={saving}
                                        icon={editingKey != null ? <SaveOutlined /> : <PlusCircleOutlined />}
                                        style={G.primaryBtn}
                                    >
                                        {editingKey != null ? `Update ${singular}` : `Add ${singular}`}
                                    </Button>

                                    {editingKey != null && (
                                        <Button
                                            onClick={handleCancel}
                                            icon={<CloseCircleOutlined />}
                                            style={{
                                                borderRadius: 10,
                                                height: 42,
                                                fontWeight: 600,
                                                border: `1px solid ${ROSE}44`,
                                                color: ROSE,
                                                background: `${ROSE}08`,
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    )}

                                    <Tooltip title="Refresh — resets all state">
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={handleRefresh}
                                            loading={loading}
                                            style={{
                                                borderRadius: 10,
                                                height: 42,
                                                border: `1px solid ${from}44`,
                                                color: from,
                                                background: `${from}08`,
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            </Form>
                        </div>
                    </div>

                    {/* Quick tips */}
                    <div
                        style={{
                            marginTop: 16,
                            background: "#fff",
                            borderRadius: 14,
                            padding: "14px 18px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#94a3b8",
                                textTransform: "uppercase",
                                letterSpacing: 0.7,
                                marginBottom: 10,
                            }}
                        >
                            Quick Tips
                        </div>
                        {[
                            { color: from, text: "Click any row in the table to edit it" },
                            { color: SKY, text: "Press Enter to move to the next field" },
                            { color: AMBER, text: `${singular} names are saved in uppercase` },
                            ...tips.map((t) => ({ color: EMERALD, text: t })),
                        ].map((tip, i) => (
                            <div
                                key={i}
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 8,
                                    marginBottom: 7,
                                    fontSize: 12,
                                    color: "#475569",
                                }}
                            >
                                <span
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: tip.color,
                                        flexShrink: 0,
                                        marginTop: 4,
                                    }}
                                />
                                {tip.text}
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>

            <style>{rowStyles(from)}</style>
        </div>
    );
}